import { CardTemplateField } from '@/hooks/useCardTemplates';

/**
 * Linked Fields Utility
 * 
 * Provides canonical key resolution for bidirectionally linked fields.
 * When fields are linked (e.g., Card Title on front linked to Card Title Copy on back),
 * this utility ensures a single "canonical" key is used for read/write across the entire UI.
 */

interface LinkedGroup {
  keys: Set<string>;
  canonicalKey: string;
}

/**
 * Build linked groups from template fields.
 * Uses union-find approach to group all linked fields together.
 */
export function buildLinkedGroups(fields: CardTemplateField[]): Map<string, LinkedGroup> {
  const parent = new Map<string, string>();
  
  // Initialize each field as its own parent
  fields.forEach(f => {
    parent.set(f.field_key, f.field_key);
  });
  
  // Find root with path compression
  const find = (key: string): string => {
    if (!parent.has(key)) {
      parent.set(key, key);
      return key;
    }
    if (parent.get(key) !== key) {
      parent.set(key, find(parent.get(key)!));
    }
    return parent.get(key)!;
  };
  
  // Union linked fields
  const union = (a: string, b: string) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) {
      parent.set(rootA, rootB);
    }
  };
  
  // Connect all linked fields
  fields.forEach(f => {
    if (f.linked_to_field_key) {
      union(f.field_key, f.linked_to_field_key);
    }
  });
  
  // Build groups by root
  const groups = new Map<string, Set<string>>();
  fields.forEach(f => {
    const root = find(f.field_key);
    if (!groups.has(root)) {
      groups.set(root, new Set());
    }
    groups.get(root)!.add(f.field_key);
    
    // Also add the linked key if it exists (might not be in fields array)
    if (f.linked_to_field_key) {
      groups.get(root)!.add(f.linked_to_field_key);
    }
  });
  
  // For each group, determine the canonical key
  const result = new Map<string, LinkedGroup>();
  
  groups.forEach((keys) => {
    const canonicalKey = selectCanonicalKey(keys, fields);
    const group: LinkedGroup = { keys, canonicalKey };
    
    // Map each key to this group
    keys.forEach(key => {
      result.set(key, group);
    });
  });
  
  return result;
}

/**
 * Select the canonical key for a group of linked fields.
 * Priority:
 * 1. Prefer keys without "copy" or timestamp suffixes
 * 2. Prefer shorter keys
 * 3. Prefer 'front' side fields
 * 4. Prefer lower display_order
 */
function selectCanonicalKey(keys: Set<string>, fields: CardTemplateField[]): string {
  const keyArray = Array.from(keys);
  
  // Build field info map for the keys we care about
  const fieldInfo = new Map<string, { side: string; order: number }>();
  fields.forEach(f => {
    if (keys.has(f.field_key)) {
      fieldInfo.set(f.field_key, {
        side: f.position_side || 'front',
        order: f.display_order || 0,
      });
    }
  });
  
  // Score each key (lower is better)
  const scored = keyArray.map(key => {
    let score = 0;
    const keyLower = key.toLowerCase();
    
    // Penalize "copy" or timestamp patterns heavily
    if (/copy[_\s]?\d*/i.test(key)) {
      score += 1000;
    }
    
    // Prefer shorter keys
    score += key.length;
    
    // Prefer 'front' side (back gets +50)
    const info = fieldInfo.get(key);
    if (info?.side === 'back') {
      score += 50;
    }
    
    // Use display_order as tiebreaker
    if (info) {
      score += info.order * 0.1;
    }
    
    return { key, score };
  });
  
  // Sort by score and return the best
  scored.sort((a, b) => a.score - b.score);
  return scored[0]?.key || keyArray[0];
}

/**
 * Resolve the canonical key for a given field key.
 * Returns the canonical key if the field is part of a linked group,
 * otherwise returns the original key.
 */
export function resolveCanonicalKey(
  fields: CardTemplateField[],
  fieldKey: string
): string {
  const groups = buildLinkedGroups(fields);
  const group = groups.get(fieldKey);
  return group?.canonicalKey || fieldKey;
}

/**
 * Get all keys in the linked group for a given field key.
 * Returns an array containing just the fieldKey if not linked.
 */
export function getGroupKeys(
  fields: CardTemplateField[],
  fieldKey: string
): string[] {
  const groups = buildLinkedGroups(fields);
  const group = groups.get(fieldKey);
  return group ? Array.from(group.keys) : [fieldKey];
}

/**
 * Check if a field key is a "copy" variant (linked copy of another field).
 * Useful for filtering out copy fields from form displays.
 */
export function isCopyField(fieldKey: string): boolean {
  return /copy[_\s]?\d*/i.test(fieldKey);
}

/**
 * Sort fields by visual position (top-to-bottom, left-to-right).
 * Used for logical tab navigation order on card previews.
 * Fields within ~5% Y-position are considered same row and sorted by X.
 */
export function sortFieldsByVisualPosition(fields: CardTemplateField[]): CardTemplateField[] {
  return [...fields].sort((a, b) => {
    // Primary: sort by Y position (top to bottom)
    const yDiff = a.position_y - b.position_y;
    // If same row (within 5% tolerance), sort by X (left to right)
    if (Math.abs(yDiff) < 5) {
      return a.position_x - b.position_x;
    }
    return yDiff;
  });
}

// Logical field order for form display (matches typical card layout flow)
const FIELD_ORDER_PRIORITY: string[] = [
  'cardtitle', 'idnumber', 'firstname', 'lastname', 'nationality', 
  'dateofbirth', 'placeofbirth', 'height', 'eyecolor', 'haircolor',
  'issuedate', 'expirydate', 'weight', 'sex', 'gender',
  'address', 'city', 'state', 'country', 'zipcode',
  'institution', 'organization', 'major', 'class',
];

/**
 * Get sort priority for a field key based on logical card layout.
 */
export function getFieldSortPriority(fieldKey: string): number {
  const normalized = fieldKey.toLowerCase().replace(/[_\s]/g, '');
  const index = FIELD_ORDER_PRIORITY.findIndex(orderKey => normalized.includes(orderKey));
  return index >= 0 ? index : FIELD_ORDER_PRIORITY.length + 1;
}

/**
 * Get deduplicated fields for form display.
 * Returns one representative field per linked group, sorted by logical card layout.
 */
export function getUniqueFieldsForForm(fields: CardTemplateField[]): CardTemplateField[] {
  const groups = buildLinkedGroups(fields);
  const seenCanonicalKeys = new Set<string>();
  const result: CardTemplateField[] = [];
  
  // Sort fields by display_order first
  const sortedFields = [...fields].sort((a, b) => a.display_order - b.display_order);
  
  for (const field of sortedFields) {
    const group = groups.get(field.field_key);
    const canonicalKey = group?.canonicalKey || field.field_key;
    
    if (!seenCanonicalKeys.has(canonicalKey)) {
      seenCanonicalKeys.add(canonicalKey);
      
      // Find the best representative field for this group
      // Prefer the one whose field_key matches the canonicalKey
      if (group && group.keys.size > 1) {
        const canonicalField = sortedFields.find(f => f.field_key === canonicalKey);
        if (canonicalField) {
          result.push(canonicalField);
        } else {
          // Fallback to current field if canonical not found in array
          result.push(field);
        }
      } else {
        result.push(field);
      }
    }
  }
  
  // Sort by logical field order priority
  result.sort((a, b) => getFieldSortPriority(a.field_key) - getFieldSortPriority(b.field_key));
  
  return result;
}
